/**
 * React Query Hook for Realtor Property Details
 * Handles data fetching, caching, and error states
 * Follows React Query best practices
 */

import React from "react";

import { useQuery } from "@tanstack/react-query";

import type {
  IPropertyDetails,
  IPropertyUser,
} from "@/components/widgets/RealtorPropertyDetails/types";
import { realtorPropertyDetailsService } from "@/services/realtor-property-details.service";

// Query key factory for consistent key management
export const realtorPropertyDetailsKeys = {
  all: ["realtor-property-details"] as const,
  property: (propertyId: string) =>
    [...realtorPropertyDetailsKeys.all, "property", propertyId] as const,
  user: (userId: string) =>
    [...realtorPropertyDetailsKeys.all, "user", userId] as const,
  propertyWithUsers: (propertyId: string) =>
    [
      ...realtorPropertyDetailsKeys.all,
      "property-with-users",
      propertyId,
    ] as const,
};

/**
 * Hook to fetch property details
 * Replaces direct fetch call in RieltorPropertyDetails component
 */
export function usePropertyDetails(
  propertyId: string | undefined,
  dealType: string | undefined,
  enabled: boolean = true,
) {
  return useQuery({
    queryKey: realtorPropertyDetailsKeys.property(propertyId || ""),
    queryFn: async () => {
      if (!propertyId) throw new Error("Property ID is required");
      if (!dealType) throw new Error("Deal type is required");
      return realtorPropertyDetailsService.getPropertyDetails({
        propertyId,
        dealType,
      });
    },
    enabled: enabled && !!propertyId && !!dealType,
    staleTime: 0, // 30 seconds - matches original component
    retry: (failureCount, error: any) => {
      // Don't retry on 404 errors
      if (error?.status === 404 || error?.code === 'PROPERTY_NOT_FOUND') {
        return false;
      }
      // More conservative retry for network issues
      return failureCount < 2;
    },
    meta: {
      errorMessage: "Failed to fetch property details",
    },
  });
}

/**
 * Hook to fetch user details
 * Used for creator and responsible person data
 */
export function useUserDetails(
  userId: string | undefined,
  enabled: boolean = true,
) {
  return useQuery({
    queryKey: realtorPropertyDetailsKeys.user(userId || ""),
    queryFn: async () => {
      if (!userId) throw new Error("User ID is required");
      return realtorPropertyDetailsService.getUserDetails({ userId });
    },
    enabled: enabled && !!userId,
    staleTime: 60_000, // 60 seconds - matches original component
    retry: (failureCount, error: any) => {
      // Don't retry on 404 errors
      if (error?.status === 404 || error?.code === "USER_NOT_FOUND") {
        return false;
      }
      return failureCount < 3;
    },
    meta: {
      errorMessage: "Failed to fetch user details",
    },
  });
}

/**
 * Hook to fetch creator details
 * Convenience hook for property creator information
 */
export function useCreatorDetails(
  createdBy: string | undefined,
  enabled: boolean = true,
) {
  return useUserDetails(createdBy, enabled);
}

/**
 * Hook to fetch responsible user details
 * Convenience hook for responsible person information
 */
export function useResponsibleUserDetails(
  responsible: string | undefined,
  enabled: boolean = true,
) {
  return useUserDetails(responsible, enabled);
}

/**
 * Main hook that combines property and user data
 * Replaces the complex useQuery logic in RieltorPropertyDetails component
 * Enhanced with better error handling and selective data fetching
 */
export function useRealtorPropertyDetails(
  propertyId: string | undefined,
  dealType: string | undefined,
  property?: IPropertyDetails,
  options: {
    enabled?: boolean;
    fetchUsers?: boolean;
  } = {},
) {
  const { enabled = true, fetchUsers = false } = options; // Disable user fetching by default to avoid CORS

  // Skip property fetching if property data is already provided to avoid CORS
  const shouldFetchProperty = enabled && !property && !!propertyId;

  const {
    data: propertyData,
    isLoading: isPropertyLoading,
    error: propertyError,
  } = usePropertyDetails(propertyId, dealType, shouldFetchProperty);

  // Resolve property data (prioritize provided property, fallback to fetched data)
  const resolvedProperty = property || propertyData;

  // Skip user fetching to avoid CORS issues - use mock data or disable
  const shouldFetchUsers = enabled && fetchUsers && !!resolvedProperty;

  const {
    data: creatorData,
    isLoading: isCreatorLoading,
    error: creatorError,
  } = useCreatorDetails(
    resolvedProperty?.creatorFields?.createdBy,
    false, // Disable to avoid CORS
  );

  const {
    data: responsibleData,
    isLoading: isResponsibleLoading,
    error: responsibleError,
  } = useResponsibleUserDetails(
    resolvedProperty?.creatorFields?.responsible,
    false, // Disable to avoid CORS
  );

  // Combine loading states
  const isLoading =
    isPropertyLoading || isCreatorLoading || isResponsibleLoading;

  // Combine error states
  const error = propertyError || creatorError || responsibleError;

  // Process data similar to original component
  const processedData = React.useMemo(() => {
    if (!resolvedProperty) return null;

    return {
      property: resolvedProperty,
      creator: creatorData || null,
      responsible: responsibleData || null,
    };
  }, [resolvedProperty, creatorData, responsibleData]);

  return {
    data: processedData,
    property: resolvedProperty,
    creator: creatorData || null,
    responsible: responsibleData || null,
    isLoading,
    error,
    // Individual loading states for granular control
    isPropertyLoading,
    isCreatorLoading,
    isResponsibleLoading,
    // Individual error states
    propertyError,
    creatorError,
    responsibleError,
  };
}

/**
 * Alternative hook using the batch service method
 * More efficient for components that need both property and users
 */
export function usePropertyWithUsers(
  propertyId: string | undefined,
  dealType: string | undefined,
  enabled: boolean = true,
) {
  return useQuery({
    queryKey: realtorPropertyDetailsKeys.propertyWithUsers(propertyId || ""),
    queryFn: async () => {
      if (!propertyId) throw new Error("Property ID is required");
      if (!dealType) throw new Error("Deal type is required");
      return realtorPropertyDetailsService.getPropertyWithUsers(
        propertyId,
        dealType,
      );
    },
    enabled: enabled && !!propertyId && !!dealType,
    staleTime: 30_000, // 30 seconds
    retry: (failureCount, error: any) => {
      // Don't retry on 404 errors
      if (error?.status === 404 || error?.code === "PROPERTY_NOT_FOUND") {
        return false;
      }
      return failureCount < 3;
    },
    meta: {
      errorMessage: "Failed to fetch property with user details",
    },
  });
}
